import { useEffect, useState } from "react";
import { apiRequest, authToken } from "../api/client";

/**
 * PWA install — the button, and counting who pressed it.
 *
 * Chrome/Edge/Samsung fire `beforeinstallprompt` once, early, and only to
 * whoever is listening at that moment. So the event is captured at startup
 * (`startInstallCapture`, from main.tsx) and held until Home renders the
 * Install card.
 *
 * An install is reported when the student accepts the browser's dialog, when
 * the browser fires `appinstalled` (covers installs from the browser menu),
 * or when the app is opened standalone (the only signal iOS gives — Safari has
 * no install event). The backend keeps one row per student, so reporting from
 * several of these is safe; the local flag just saves the round trip.
 */

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
}

export type InstallPlatform = "android" | "ios" | "desktop";

const REPORTED_KEY = "prepai.install.reported";
const PENDING_KEY = "prepai.install.pending";

let deferredPrompt: BeforeInstallPromptEvent | null = null;
const listeners = new Set<() => void>();
const notify = () => listeners.forEach((listener) => listener());

function storage(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function setStorage(key: string, value: string | null): void {
  try {
    if (value === null) localStorage.removeItem(key);
    else localStorage.setItem(key, value);
  } catch {
    /* Private mode — we may report twice; the backend dedupes. */
  }
}

export function detectPlatform(userAgent = navigator.userAgent): InstallPlatform {
  if (/iphone|ipad|ipod/i.test(userAgent)) return "ios";
  if (/android/i.test(userAgent)) return "android";
  return "desktop";
}

export function isStandalone(): boolean {
  const iosStandalone = (navigator as Navigator & { standalone?: boolean }).standalone === true;
  const displayStandalone =
    typeof window.matchMedia === "function" &&
    window.matchMedia("(display-mode: standalone)").matches;
  return iosStandalone || displayStandalone;
}

/**
 * Send the install to the backend, once per device.
 *
 * Signed out (installed from the landing page, say), the install is parked and
 * sent by `flushPendingInstall` after sign-in, so it is still tied to a student.
 */
export async function reportInstall(platform: InstallPlatform = detectPlatform()): Promise<void> {
  if (storage(REPORTED_KEY)) return;
  if (!authToken.get()) {
    setStorage(PENDING_KEY, platform);
    return;
  }
  try {
    await apiRequest("/api/me/app-install", {
      method: "POST",
      body: { platform },
      authenticated: true,
    });
    setStorage(REPORTED_KEY, "1");
    setStorage(PENDING_KEY, null);
  } catch {
    // Offline or a server hiccup: keep it pending and try on the next open.
    setStorage(PENDING_KEY, platform);
  }
}

/** Called once a student is signed in: sends a parked install, or a standalone launch. */
export async function flushPendingInstall(): Promise<void> {
  const pending = storage(PENDING_KEY) as InstallPlatform | null;
  if (pending) return reportInstall(pending);
  if (isStandalone()) return reportInstall();
}

export function startInstallCapture(): void {
  window.addEventListener("beforeinstallprompt", (event) => {
    // Stop the mini-infobar; we show our own button instead.
    event.preventDefault();
    deferredPrompt = event as BeforeInstallPromptEvent;
    notify();
  });

  window.addEventListener("appinstalled", () => {
    deferredPrompt = null;
    notify();
    void reportInstall();
  });
}

/** Test seam — the module holds the captured prompt between renders. */
export function resetInstallState(): void {
  deferredPrompt = null;
  notify();
}

export function useInstallPrompt() {
  const [canPrompt, setCanPrompt] = useState(deferredPrompt !== null);
  const [installed, setInstalled] = useState(() => isStandalone());

  useEffect(() => {
    const update = () => setCanPrompt(deferredPrompt !== null);
    listeners.add(update);
    update();
    return () => {
      listeners.delete(update);
    };
  }, []);

  const platform = detectPlatform();

  const install = async (): Promise<boolean> => {
    const prompt = deferredPrompt;
    if (!prompt) return false;
    await prompt.prompt();
    const { outcome } = await prompt.userChoice;
    // The prompt can be shown once; after that the browser needs a new event.
    deferredPrompt = null;
    notify();
    if (outcome !== "accepted") return false;
    setInstalled(true);
    await reportInstall(platform);
    return true;
  };

  return {
    /** The browser offered a native install dialog we can open. */
    canPrompt,
    /** iOS Safari: no dialog, so the card shows Add to Home Screen steps. */
    needsManualSteps: platform === "ios" && !installed,
    installed,
    install,
  };
}
